<template>
  <div>
    <div class="k8m-node-table-title">
      <span class="k8m-node-title">
        <span>
          {{ $t('k8.nodesCap') }}
        </span>
        <span v-if="displayTotal"> ( {{ total || 0 }} ) </span>
      </span>
      <div class="k8m-node-view-toggle" v-if="enableToggle">
        <RexToggle
          :label="toggleData"
          :customStyle="{ height: '2rem', width: '20rem', fontSize: '1rem' }"
          :updateOnClick="true"
          @onChange="onClickToggle"
          id="k8m-node-toggle-status"
          :iconNames="{
            isIconEnabled: true,
            icon2: {
              name: 'lineChart',
              color: 'rgb(245, 245, 245)',
            },
            icon1: {
              name: 'topoTable',
              color: 'rgb(245, 245, 245)',
            },
          }"
        />
      </div>
      <div v-if="enableViewIcon" class="k8m-node-header-button">
        <RexButtonV3
          id="k8m-node-btn-view-all"
          class="k8m-node-title-button-view-all"
          variant="transparent"
          :styling="{ width: 'none' }"
          @onClick="showAllNodes"
        >
          <div class="k8m-node-header-button-text">
            {{ $t('viewAll') }}
          </div>
          <BaseIcon name="arrow-narrow-right" class="k8m-node-arrow-icon" />
        </RexButtonV3>
      </div>
      <div v-if="showSearchBar" class="k8-tb-search">
        <RexSearch
          :placeholder="searchPlaceholder"
          @search="handleSearch"
          @clearSelection="clearSearch"
        />
      </div>
    </div>
    <div v-show="isTable">
      <div class="k8m-node-chart-table-element">
        <TableBuilder
          :tableConfig="tableConfigs"
          :scope="scope"
          :height="height"
          :enableInfiniteScroll="enableInfiniteScroll"
          @getNodesTotal="nodesTotal"
          :exclusiveSearchKeyword="keyword"
          :defaultSortKey="getDefaultSortKey"
        />
      </div>
    </div>
    <div class="k8m-node-chart-wrapper" v-show="!isTable">
      <div
        class="k8m-node-chart-element"
        v-for="(item, index) in graphData"
        :key="'line' + index"
      >
        <MultiLineChart
          :legendHeaders="mapConfigs[index].headers"
          :item="mapConfigs[index]"
          :data="
            graphData[index] && graphData[index][mapConfigs[index].metricName]
              ? graphData[index][mapConfigs[index].metricName].value
              : []
          "
          highLightColValue="1"
          :intervalTime="intervalTime"
          @openModal="openGraphModal"
          :chartId="`chart-${mapConfigs[index].metricName}`"
          :title="mapConfigs[index].title"
          :loading="loading"
          :graphIndex="index"
          :formula="mapConfigs[index].formula"
          :Graphlabels="mapConfigs[index].graphTitles"
        />
      </div>
    </div>
    <MultiChartModal
      :legendHeaders="mapConfigs[selectGraphIndex].headers"
      :item="mapConfigs[selectGraphIndex]"
      :data="
        graphData[selectGraphIndex] &&
        graphData[selectGraphIndex][mapConfigs[selectGraphIndex].metricName]
          ? graphData[selectGraphIndex][mapConfigs[selectGraphIndex].metricName]
              .value
          : []
      "
      highLightColValue="1"
      :intervalTime="intervalTime"
      :showGraphModal="showGraphModal"
      @onHide="showGraphModal = false"
      :chartId="`chart-${mapConfigs[selectGraphIndex].metricName}`"
      infoSpanColIndex="0"
      :title="mapConfigs[selectGraphIndex].title"
      @closeGraphModal="showGraphModal = false"
      :formula="(value) => value.toFixed(3)"
      :Graphlabels="mapConfigs[selectGraphIndex].graphTitles"
    />
  </div>
</template>
<script>
import TableBuilder from '@/components/K8M/Common/TableBuilder.vue';
import MultiLineChart from '@/components/charts/multi-chart.vue';
import MultiChartModal from '@/components/charts/multi-chart-modal';
import RexButtonV3 from '@/rex-components/RexButtonV3.vue';
import RexToggle from '@/rex-components/RexToggle.vue';
import RexSearch from '@/rex-components/rex-search.vue';
import { k8Scopes, DELIMITER } from '@/store/modules/k8/k8-types';
import { mapGetters, mapMutations, mapState } from 'vuex';
import { K8_NAVTABS } from '@/utils/templates/k8mTemplate/k8m.constants';
import { K8Routes } from '@/components/K8M/k8.types';
import graphQl from '@/services/graphQl';
import graphHelpers from '@/utils/graphHelper';

export default {
  name: 'ClusterNodeOverview',
  components: {
    TableBuilder,
    RexToggle,
    RexButtonV3,
    MultiLineChart,
    MultiChartModal,
    RexSearch,
  },
  data() {
    return {
      graphData: [],
      scope: k8Scopes.SERVICE,
      isTable: true,
      style: { height: '2rem', width: '15rem', fontSize: '1rem' },
      selectGraphIndex: 0,
      showGraphModal: false,
      loading: false,
      total: 0,
      keyword: '',
    };
  },
  props: {
    height: {
      type: String,
      default: '20rem',
    },
    tableConfigs: {
      type: Object,
      default: () => ({}),
    },
    mapConfigs: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapGetters('globalState/settings', [
      'getDurationTime',
      'intervalTime',
      'getCompareDurationTime',
    ]),
    ...mapGetters('k8State/k8CommonState', ['getCurrentK8Cluster']),
    ...mapState('k8State/k8CommonState', ['k8NodeEntities']),
    ...mapState('globalState/settings', ['isCompareOptionsSelected']),
    enableInfiniteScroll() {
      return this.$route.path.includes('nodes') ? true : false;
    },
    displayTotal() {
      return this.$route.name === K8Routes.nodes;
    },
    enableViewIcon() {
      return !this.$route.path.includes('nodes') && this.isTable;
    },
    enableToggle() {
      return this.$route.name !== K8Routes.nodes;
    },
    getEntityIds() {
      return this.k8NodeEntities.map((entity) => entity.key);
    },
    showSearchBar() {
      return this.tableConfigs.showSearch && this.$route.path.includes('nodes');
    },
    searchPlaceholder() {
      return this.tableConfigs.searchPlaceholder;
    },
    getDefaultSortKey() {
      return this.tableConfigs.defaultSortKey;
    },
    toggleData() {
      return [this.$t('k8.tableView'), this.$t('k8.mapView')];
    },
  },
  methods: {
    ...mapMutations('k8State/k8CommonState', ['setCurrentK8Tab']),
    onClickToggle(data) {
      this.isTable = !data;
    },
    nodesTotal(data) {
      this.total = data;
    },
    showAllNodes() {
      this.$router.push({
        name: K8Routes.nodes,
      });
      this.setCurrentK8Tab(K8_NAVTABS.K8M_NODE_TAB);
    },
    openGraphModal(index) {
      this.selectGraphIndex = index;
      this.showGraphModal = true;
    },
    async getData() {
      const apis = [];
      this.loading = true;
      this.mapConfigs.forEach((item) => {
        const metricNames = graphHelpers.parse(item.metricName);
        metricNames.forEach((metric) => {
          const params = {
            condition: {
              name: metric,
              entity: {
                scope: 'Service',
                serviceName: this.getCurrentK8Cluster?.key ?? '',
                normal: true,
              },
            },
            valueColumn: item.valueColumn,
            properties: [
              {
                key: 'instanceType',
                value: 'K8S_NODE',
              },
            ],
            entityIds: this.getEntityIds,
          };
          const paramsCurrent = {
            ...params,
            duration: this.getDurationTime,
          };
          const paramsCompare = {
            ...params,
            durationCurrent: this.getDurationTime,
            durationCompare: this.getCompareDurationTime,
          };
          if (this.isCompareOptionsSelected) {
            apis.push(
              graphQl.query('getK8MultiChartCompare').params(paramsCompare),
            );
          } else {
            apis.push(graphQl.query('getK8MultiChart').params(paramsCurrent));
          }
        });
      });

      const graphsData = await Promise.allSettled(apis);
      this.graphData = [];
      let graphIndex = 0;

      if (this.isCompareOptionsSelected) {
        this.mapConfigs.forEach((graph) => {
          const metricNameList = graphHelpers.parse(graph.metricName);
          const combinedData = {};

          metricNameList.forEach((metric) => {
            const processMetricValues = (values, labelPrefix) =>
              values.map((obj) => {
                const updatedLabel = obj.label.includes(DELIMITER)
                  ? obj.label.split(DELIMITER).pop()
                  : obj.label;
                return {
                  ...obj,
                  label: `${labelPrefix} period - ${updatedLabel}`,
                };
              });

            const metricValues = processMetricValues(
              graphsData[graphIndex]?.value?.data?.data
                ?.readAllMetricsFilteredAtServiceScope ?? [],
              'Current',
            );
            const metricValuesCompared = processMetricValues(
              graphsData[graphIndex]?.value?.data?.data?.compared ?? [],
              'Previous',
            );

            const combinedValues = [];
            const maxLength = Math.max(
              metricValues.length,
              metricValuesCompared.length,
            );

            for (let i = 0; i < maxLength; i++) {
              if (metricValues[i]) combinedValues.push(metricValues[i]);

              if (metricValuesCompared[i]) {
                const matchedCompared = metricValuesCompared.find(
                  (item) =>
                    item.label.replace('Previous period', '').trim() ===
                    metricValues[i].label.replace('Current period', '').trim(),
                );
                if (matchedCompared) {
                  combinedValues.push(matchedCompared);
                }
              }
            }

            combinedData[metric] = {
              key: 'Nodes',
              value: combinedValues,
            };

            graphIndex++;
          });
          this.graphData.push(combinedData);
        });
      } else {
        this.mapConfigs.forEach((graph) => {
          const metricNameList = graphHelpers.parse(graph.metricName);
          const tempGraphData = {};
          metricNameList.forEach((metric) => {
            let metricValues =
              graphsData[graphIndex++]?.value?.data?.data
                ?.readAllMetricsFilteredAtServiceScope ?? [];
            metricValues = metricValues.map((obj) => {
              const labelSplit = obj.label.split(DELIMITER);
              return {
                ...obj,
                label: obj.label.includes(DELIMITER)
                  ? labelSplit[labelSplit.length - 1]
                  : obj.label,
              };
            });
            tempGraphData[metric] = {
              key: 'Nodes',
              value: metricValues,
            };
          });
          this.graphData.push(tempGraphData);
        });
      }
      this.loading = false;
    },
    handleSearch(data) {
      this.keyword = data;
    },
    clearSearch() {
      this.keyword = '';
    },
  },
  watch: {
    getDurationTime() {
      if (!this.isTable) {
        this.getData();
      }
    },
    k8NodeEntities() {
      this.getData();
    },
    isCompareOptionsSelected() {
      this.getData();
    },
  },
};
</script>

<style lang="scss" scoped>
.k8m-node-chart-element {
  width: 49%;
  margin-bottom: 1.5em;
  margin-top: 0.125em;
  margin-left: 0.125em;
}
.k8m-node-chart-table-element {
  width: 100%;
  margin-bottom: 1.5em;
  margin-top: 0.125em;
  margin-left: 0.125em;
}
:deep(.header-container) {
  font-size: 1rem;
}
.k8m-node-table-title {
  @include flex-center-justify(space-between);
  height: 3rem;
  font-size: 1.25rem;
  margin-top: 1rem;
  width: 100%;
  color: $font-primary;
  margin-bottom: 1rem;
}
.k8m-node-header-button {
  margin-right: 1rem;
}

.k8m-node-title-button-view-all {
  height: 1.43rem;
  text-align: center;
  align-items: center;
  display: flex;
  justify-content: flex-end;
  &:hover {
    text-decoration: underline;
  }
  @include dropshadow-effect(0);
  border: none;
}

.k8m-node-view-toggle {
  position: relative;
  margin-right: auto;
  width: fit-content;
}

.k8m-node-title {
  margin-right: 1rem;
  font-size: 1.25rem;
  font-family: $semi-bold;
}

:deep(.toggle-parent) {
  border: 1px solid rgba(105, 105, 105, 1);
}

:deep(.toggle-parent .active-class) {
  background-color: rgba(105, 105, 105, 1);
  color: $font-primary;
}

:deep(.toggle-parent .inactive-class) {
  color: $font-primary;
}

.k8m-node-header-button-text {
  font-family: RakutenSansUI-Regular !important;
  color: $color-brand-accent;
  font-size: 1rem;
  margin-right: 0.3rem;
}

.k8m-node-chart-wrapper {
  width: 100%;
  @include flex-center-justify(space-between);
  flex-wrap: wrap;
  margin-top: 1.4em;
  margin-right: 1rem;
}

.k8m-node-arrow-icon {
  padding-bottom: 0.3rem;
  @include svg-fill($color-brand-accent);
}

.k8-tb-search {
  @include flex-column;
  align-items: flex-end;
  margin-top: 1.0625rem;
  margin-bottom: 1.25rem;
  margin-right: 1rem;
}
</style>
